import { faker } from "@faker-js/faker/locale/en_US";
import moment from "moment";
import {
  IHostTrendingCustomerScore,
  IHostDashboardLayout,
  IHostEvent,
  IHostPlayerTierSumamry,
  IHostPromotion,
  IHostTrip,
  HostFavoritePlayer,
  IHostPromotionDetail,
} from "services/models/Host";
import { IHostActivePlayer } from "services/models/Host/HostActivePlayer";
import { IHostPlayerAudit } from "services/models/Host/HostGuestAudit";
import { IHostPlayer } from "services/models/Host/HostPlayer";
import { IHostReservation } from "services/models/Host/HostReservation";
import { abortableDelay, delay, IAbortablePromise } from "utils";
import {
  getRandom,
  getRandomArray,
  getRandomBirthday,
  getRandomDecimal,
  getRandomInt,
  getRandomNegativeNumber,
} from "./data";
import { mockReservations } from "./reservations-data";
import { en } from "@faker-js/faker";

function createTier(name: string, color: string) {
  return {
    name,
    color,
  };
}

export const tiers = [
  createTier("Classic", "#DA4240"),
  createTier("Emerald", "#86B05B"),
  createTier("Amber", "#E3A02F"),
  createTier("Diamond", "#7F7E7F"),
  createTier("Turquoise", "#00AAB9"),
  createTier("Yuhaaviatam", "#FEE632"),
];

const DefaultHosts = ["John Win", "Jill Valentine", "Nick Meter", "Baoquan Donh"];

const Days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

interface IMockHostReservation extends IHostReservation {
  status: string;
}

interface IMockHostTrip extends IHostTrip {
  type: string;
}

export function randomNullableNumber(start: number, end: number) {
  return faker.datatype.boolean() ? getRandomInt(start, end) : null;
}

class MockHostDataStore {
  getRandomHost() {
    return getRandom(DefaultHosts);
  }

  players: IHostPlayer[] = getRandomArray(100, 800).map(() => {
    const tierIndex = faker.number.int({ min: 0, max: tiers.length - 1 });
    return {
      clubNumber: faker.number.int({ min: 10000, max: 99999 }),
      firstName: faker.person.firstName(),
      lastName: faker.person.lastName(),
      hostName: getRandom(DefaultHosts),
      birthdate: getRandomBirthday(),
      currentTier: tiers[tierIndex].name,
      currentTierColor: tiers[tierIndex].color,
    };
  });

  hostPlayerAudit: IHostPlayerAudit[] = getRandomArray(20, 100).map(() => {
    const tierIndex = faker.number.int({ min: 0, max: tiers.length - 1 });
    const firstName = faker.person.firstName();
    const lastName = faker.person.lastName();
    const currentHostName = getRandom(DefaultHosts);

    return {
      clubNumber: faker.number.int({ min: 10000, max: 99999 }).toString(),
      firstName: firstName,
      lastName: lastName,
      fullName: `${firstName} ${lastName}`,
      hostName: getRandom(DefaultHosts),
      previousHostName: getRandom(DefaultHosts.filter((host) => host !== currentHostName)),
      currentHostName: currentHostName,
      dateTimeOfReassignment: faker.date.past().toISOString(),
      currentTier: tiers[tierIndex].name,
      currentTierColor: tiers[tierIndex].color,
    };
  });

  activePlayers: IHostActivePlayer[] = getRandomArray(0, 0).map(() => {
    const tierIndex = faker.number.int({ min: 0, max: tiers.length - 1 });
    const firstName = faker.person.firstName();
    const lastName = faker.person.lastName();
    return {
      clubNumber: faker.number.int({ min: 10000, max: 99999 }),
      firstName: firstName,
      lastName: lastName,
      fullName: `${firstName} ${lastName}`,
      adt90Day: randomNullableNumber(1, 500),
      adw90Day: randomNullableNumber(1, 500),
      avt: getRandomNegativeNumber(parseFloat(getRandomDecimal(0, 7).toFixed(2))),
      activity: faker.company.catchPhraseNoun(),
      activityLocation: faker.location.city(),
      activityTimestamp: faker.date.between({ from: "2022-07-01T00:00:00.000Z", to: moment().toString() }).toString(),
      hostName: getRandom(DefaultHosts),
      currentTier: tiers[tierIndex].name,
      currentTierColor: tiers[tierIndex].color,
    };
  });

  favoritePlayers: HostFavoritePlayer[] = getRandomArray(2, 10).map(() => {
    const tierIndex = faker.number.int({ min: 0, max: tiers.length - 1 });

    return {
      fullName: `${faker.person.firstName()} ${faker.person.lastName()}`,
      adt90Day: randomNullableNumber(1, 500),
      adw90Day: randomNullableNumber(1, 500),
      avt: getRandomNegativeNumber(parseFloat(getRandomDecimal(0, 7).toFixed(2))),
      activity: faker.company.catchPhraseNoun(),
      activityLocation: faker.location.city(),
      activityTimestamp: faker.date.between({ from: "2022-07-01T00:00:00.000Z", to: moment().toString() }).toString(),
      isOnTheFloor: faker.datatype.boolean(),
      clubNumber: faker.number.int({ min: 10000, max: 99999 }),
      hostName: getRandom(DefaultHosts),
      currentTier: tiers[tierIndex].name,
      currentTierColor: tiers[tierIndex].color,
    };
  });

  trips: IMockHostTrip[] = getRandomArray(20, 100).map(() => {
    const tierIndex = faker.number.int({ min: 0, max: tiers.length - 1 });
    const firstName = faker.person.firstName();
    const lastName = faker.person.lastName();
    const type = getRandom(["increasing", "decreasing"]);
    return {
      clubNumber: faker.number.int({ min: 10000, max: 99999 }).toString(),
      firstName: firstName,
      lastName: lastName,
      fullName: `${firstName} ${lastName}`,
      hostName: this.getRandomHost(),
      type: type,
      averageVisits6Months: parseFloat(getRandomDecimal(2, 20).toFixed(2)),
      visitsLast30Days: parseFloat(getRandomDecimal(2, 6).toFixed(2)),
      currentTier: tiers[tierIndex].name,
      currentTierColor: tiers[tierIndex].color,
    };
  });

  customerValueScores: IHostTrendingCustomerScore[] = getRandomArray(20, 100).map(() => {
    const tierIndex = faker.number.int({
      min: 0,
      max: tiers.length - 1,
    });
    const firstName = faker.person.firstName();
    const lastName = faker.person.lastName();
    return {
      clubNumber: faker.number.int({ min: 10000, max: 99999 }).toString(),
      firstName: firstName,
      lastName: lastName,
      fullName: `${firstName} ${lastName}`,
      hostName: this.getRandomHost(),
      currentTierColor: tiers[tierIndex].color,
      currentCustomerValueScore: getRandomInt(10, 50),
      average30DayCustomerValueScore: getRandomInt(10, 50),
      average90DayCustomerValueScore: getRandomInt(10, 50),
    };
  });

  // Uses reservations that is already randomly generated in mock-reservation-data
  reservations: IMockHostReservation[] = mockReservations.reservations.map((reservation) => {
    const tierIndex = faker.number.int({
      min: 0,
      max: tiers.length - 1,
    });
    const isActive = getRandom(["active", "upcoming"]);
    return {
      fullName: faker.person.fullName(),
      hostName: getRandom(DefaultHosts),
      currentTier: tiers[tierIndex].name,
      currentTierColor: tiers[tierIndex].color,
      checkIn: reservation.checkIn.toString(),
      checkOut: reservation.checkOut.toString(),
      status: isActive,
      confirmationNumber: reservation.confirmationNumber,
      clubNumber: faker.number.int({ min: 10000, max: 99999 }),
    };
  });

  promotions: IHostPromotion[] = getRandomArray(50, 100).map((i) => {
    return {
      id: getRandomInt(1, 99999).toString(),
      name: "promotions -" + faker.word.noun(),
      start: faker.date.future({ years: 2 }).toISOString(),
      end: faker.date.future({ years: 3 }).toISOString(),
      categoryName: faker.company.buzzNoun(),
      active: getRandom(Days),
      inviteOnly: getRandom([true, false]),
      type: getRandom(["Promotion", "Event"]),
    };
  });

  events: IHostEvent[] = getRandomArray(20, 100).map((i) => {
    return {
      id: getRandomInt(1, 99999).toString(),
      name: "events -" + faker.word.noun(),
      start: faker.date.future({ years: 2 }).toISOString(),
      end: faker.date.future({ years: 3 }).toISOString(),
      categoryName: faker.company.buzzNoun(),
      active: "u, w, w, r, s",
      inviteOnly: getRandom([true, false]),
    };
  });

  playerTierSummary: IHostPlayerTierSumamry = {
    tiers: this.activePlayers.map((player) => {
      return {
        tierLevel: player.currentTier,
        hosted: getRandomInt(3, 10),
        total: getRandomInt(400, 9999),
        primaryColor: player.currentTierColor,
      };
    }),
    nonCarded: getRandomInt(100, 200),
    totalCarded: getRandomInt(100, 300),
    totalHosted: getRandomInt(100, 400),
  };
  // Randomly generated reservations
  // getRandomArray(10, 50).map(() => {
  //   const tierIndex = faker.datatype.number({ min: 0, max: tiers.length - 1 });
  //   const isActive = getRandom(["active", "upcoming"]);
  //   const checkIn =
  //     isActive === "active"
  //       ? moment().add(getRandomInt(-5, -2), "days")
  //       : moment().add(getRandomInt(2, 5), "days");
  //   const checkOut = moment(checkIn).add(getRandomInt(2, 5), "days");
  //   return {
  //     fullName: faker.name.fullName(),
  //     hostedBy: getRandom(defaultHosts),
  //     currentTier: tiers[tierIndex].name,
  //     currentTierColor: tiers[tierIndex].color,
  //     checkIn: checkIn.toString(),
  //     checkOut: checkOut.toString(),
  //     status: isActive,
  //     confirmationNumber: getRandomInt(11111111, 99999999).toString(),
  //     clubNumber: faker.datatype.number({ min: 10000, max: 99999 }),
  //   };
  // });

  dashboardLayouts: IHostDashboardLayout[] = getRandomArray(1, 10).map(() => {
    return {
      i: faker.company.buzzNoun(),
      x: getRandom([0, 5]),
      y: getRandomInt(0, 10),
      w: getRandom([5, 10]),
      h: 4,
      isDisplayed: getRandom([true, false]),
    };
  });

  getGuestAndHostAudit(): IAbortablePromise<IHostPlayerAudit[]> {
    return abortableDelay(() => {
      return this.hostPlayerAudit;
    });
  }

  getPlayerBirthdays(days?: string): IAbortablePromise<IHostPlayer[]> {
    return abortableDelay(() => {
      if (days) {
        return this.players.filter((player) => {
          const tempDate = moment(player.birthdate).set("year", moment().year());
          return tempDate.isBetween(moment(), moment().add(30, "days"));
        });
      }
      return this.players;
    });
  }

  getActivePlayers(): IAbortablePromise<IHostActivePlayer[]> {
    return abortableDelay(() => {
      return this.activePlayers;
    });
  }

  getCustomerValueScore(): IAbortablePromise<IHostTrendingCustomerScore[]> {
    return abortableDelay(() => {
      return this.customerValueScores;
    });
  }

  getReservationsByStatus(status: string): IAbortablePromise<IHostReservation[]> {
    return abortableDelay(() => {
      return this.reservations.filter((r) => r.status === status);
    });
  }

  getDashboardLayout(): Promise<IHostDashboardLayout[]> {
    return delay(() => {
      return this.dashboardLayouts;
    });
  }

  getPromotions(): IAbortablePromise<IHostPromotion[]> {
    return abortableDelay(() => {
      return this.promotions;
    });
  }

  getPromotion(pid: string): Promise<IHostPromotionDetail> {
    return delay(() => {
      const match: IHostPromotion | undefined = this.promotions.find((p) => p.id === pid);
      if (!match) {
        throw new Error(`No promotion found with ${pid}`);
      }

      const { start, end, id, name } = match;

      return {
        dates: [
          {
            start,
            end,
            isAllDay: faker.datatype.boolean(),
          },
        ],
        id: Number(id),
        title: name,
        description: faker.commerce.productDescription(),
        canShare: faker.datatype.boolean(),
        canDownload: faker.datatype.boolean(),
        contact: {
          displayName: faker.person.fullName(),
          email: faker.internet.email(),
          phone: faker.phone.number(),
        },
        categoryName: faker.commerce.productAdjective(),
        actions: [
          {
            title: faker.company.buzzVerb(),
            shortTitle: faker.company.name(),
            action: {
              type: faker.company.buzzVerb(),
              value: faker.company.name(),
            },
          },
        ],
        imageUrls: [faker.image.url()],
        topSections: [
          {
            title: faker.company.buzzNoun(),
            paragraph: faker.company.catchPhrase(),
          },
        ],
        bottomSections: [
          {
            title: faker.company.buzzNoun(),
            paragraph: faker.company.catchPhrase(),
          },
        ],
      };
    });
  }

  getEvents(): IAbortablePromise<IHostEvent[]> {
    return abortableDelay(() => {
      return this.events;
    });
  }
  getTrips(value: string): IAbortablePromise<IHostTrip[]> {
    const sorterFunction = (a: IHostTrip, b: IHostTrip) => {
      if (value === "decreasing") {
        return a.averageVisits6Months - b.averageVisits6Months;
      }
      return b.averageVisits6Months - a.averageVisits6Months;
    };
    return abortableDelay(() => {
      return this.trips.filter((t) => t.type === value).sort(sorterFunction);
    });
  }

  getPlayerTierSummary(): IAbortablePromise<IHostPlayerTierSumamry> {
    return abortableDelay(() => {
      return this.playerTierSummary;
    });
  }

  followPlayer(playerId: number): Promise<void> {
    return delay(() => {
      const tierIndex = faker.number.int({ min: 0, max: tiers.length - 1 });

      this.favoritePlayers.push({
        fullName: `${faker.person.firstName()} ${faker.person.lastName()}`,
        adt90Day: randomNullableNumber(1, 500),
        adw90Day: randomNullableNumber(1, 500),
        avt: getRandomNegativeNumber(parseFloat(getRandomDecimal(0, 7).toFixed(2))),
        activity: faker.company.catchPhraseNoun(),
        activityLocation: faker.location.city(),
        activityTimestamp: faker.date.between({ from: "2022-07-01T00:00:00.000Z", to: moment().toString() }).toString(),
        isOnTheFloor: faker.datatype.boolean(),
        clubNumber: playerId,
        hostName: getRandom(DefaultHosts),
        currentTier: tiers[tierIndex].name,
        currentTierColor: tiers[tierIndex].color,
      });
    });
  }

  unfollowPlayer(playerId: number): Promise<void> {
    return delay(() => {
      const removedArr = this.favoritePlayers.filter((f) => f.clubNumber !== playerId);
      this.favoritePlayers = removedArr;
    });
  }
}

export const mockHostDataStore = new MockHostDataStore();
